<script lang="ts" setup>
import FilterTable from "@/components/Common/Table/FilterTable.vue";
import { ref } from "vue";
import type { Header } from "vue3-easy-data-table";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import Swal from "sweetalert2";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { useVesselClass } from "@/stores/master/vessel-class";
import { hasAccess } from "@/utils/helper";

const linkAdd = {
  text: "Add Vessel Class",
  url: "/master/vessel-class/create",
};

const headers: Header[] = [
  { text: "NAME", value: "name" },
  { text: "CODE", value: "code" },
  { text: "YEAR", value: "year" },
  { text: "HEAD OFFICE", value: "head_office" },
  { text: "IACS MEMBER", value: "iacs_member" },
  { text: "ACTIONS", value: "action" },
];

const vesselClassStore = useVesselClass();
const page = ref<number>(1);
const limit = ref<number>(10);
const totalItems = ref<number>(0);
const listRole = ref<any>([]);
const isLoading = ref(false);

const loadDataVesselClass = async (query = "") => {
  isLoading.value = true;
  const { success, message } = await vesselClassStore.dispatchGetAll(
    page.value,
    limit.value,
    query
  );
  if (success) {
    listRole.value = await vesselClassStore.vesselClassList.items;
    page.value = await vesselClassStore.vesselClassList.page;
    limit.value = await vesselClassStore.vesselClassList.pageSize;
    totalItems.value = await vesselClassStore.vesselClassList.total;
  }
  isLoading.value = false;
};

const handleServerOptionsUpdate = (options: {
  page: number;
  rowsPerPage: number;
}) => {
  page.value = options.page;
  limit.value = options.rowsPerPage;
  loadDataVesselClass();
};

const handleEdit = (vesselType: any) => {
  router.push("/master/vessel-class/" + vesselType);
};

const handleDelete = async (vesselType: any) => {
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      cancelButton: "btn btn-danger",
      confirmButton: "btn btn-success",
    },
    buttonsStyling: false,
  });
  swalWithBootstrapButtons
    .fire({
      title: `Are you sure want to delete data ${vesselType.name} ?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    })
    .then(async (result) => {
      if (result.isConfirmed) {
        const { success, message } = await vesselClassStore.dispatchDelete(
          vesselType.id
        );
        if (success) {
          swalWithBootstrapButtons.fire({
            title: "Deleted!",
            text: "Your file has been deleted.",
            icon: "success",
          });
          loadDataVesselClass();
        } else {
          toast.error(message);
        }
      } else if (
        /* Read more about handling dismissals below */
        result.dismiss === Swal.DismissReason.cancel
      ) {
        swalWithBootstrapButtons.fire({
          title: "Cancelled",
          text: "Your action has been aborted",
          icon: "error",
        });
      }
    });
};

loadDataVesselClass();
</script>
<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <div
        class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
      >
        <h3 class="mb-0">List Data Vessel Class</h3>
      </div>
      <FilterTable :link="linkAdd" @searchByQuery="loadDataVesselClass" />
      <div class="table-content">
        <BaseLoading v-if="isLoading" />
        <EasyDataTable
          :headers="headers"
          :items="listRole"
          v-if="!isLoading"
          show-index
          :server-options="{
            page: page,
            rowsPerPage: limit,
          }"
          :server-items-length="totalItems"
          @update:server-options="handleServerOptionsUpdate"
          buttons-pagination
        >
          <template #item-iacs_member="props">
            <div v-if="props.iacs_member == 1">
              <span class="badge bg-primary">Yes</span>
            </div>
            <div v-else>
              <span class="badge bg-danger">No</span>
            </div>
          </template>
          <template #item-action="props">
            <div class="d-flex gap-2">
              <button
                v-if="hasAccess('vessel_class-update')"
                class="btn btn-info btn-sm text-white"
                @click="handleEdit(props.id)"
              >
                Edit
              </button>
              <button
                v-if="hasAccess('vessel_class-delete')"
                class="btn btn-danger btn-sm"
                @click="handleDelete(props)"
              >
                Delete
              </button>
            </div>
          </template>
        </EasyDataTable>
      </div>
    </div>
  </div>
</template>
